import React from 'react';
import { motion } from 'framer-motion';
import { Check, Plus, Minus, AlertCircle } from 'lucide-react';
import {
  ProductOptionGroup,
  ProductOption,
  SelectedOption,
  OptionGroupType,
  getOptionName,
} from '../types/customization';
import { MenuTheme } from '../types';

interface OptionGroupSelectorProps {
  optionGroup: ProductOptionGroup;
  selectedOptions: SelectedOption[];
  onChange: (selections: SelectedOption[]) => void;
  language?: string;
  error?: string;
  theme?: MenuTheme;
}

export default function OptionGroupSelector({
  optionGroup,
  selectedOptions,
  onChange,
  language = 'it',
  error,
  theme,
}: OptionGroupSelectorProps) {
  // Theme colors with defaults
  const colors = {
    primary: theme?.primaryColor || '#f97316',
    background: theme?.backgroundColor || '#0a0a0a',
    card: theme?.cardBackground || '#141418',
    text: theme?.textColor || '#ffffff',
    textMuted: theme?.mutedColor || '#a1a1aa',
    border: theme?.borderColor || '#27272a',
  };

  const groupSelections = selectedOptions.filter(
    (s) => s.optionGroupId === optionGroup.id
  );

  const isOptionSelected = (optionId: string): boolean => {
    return groupSelections.some((s) => s.optionId === optionId);
  };

  const getOptionQuantity = (optionId: string): number => {
    const selection = groupSelections.find((s) => s.optionId === optionId);
    return selection?.quantity || 0;
  };

  const handleSingleChoice = (option: ProductOption) => {
    if (!option.available) return;

    const otherGroupSelections = selectedOptions.filter(
      (s) => s.optionGroupId !== optionGroup.id
    );

    // If already selected, deselect it (unless required)
    if (isOptionSelected(option.id)) {
      // Allow deselection only if not required
      if (!optionGroup.required) {
        onChange([...otherGroupSelections]);
      }
      return;
    }

    const newSelection: SelectedOption = {
      optionGroupId: optionGroup.id,
      optionGroupName: optionGroup.name,
      optionId: option.id,
      optionName: option.name,
      quantity: 1,
      priceModifier: option.priceModifier,
      ingredientId: option.ingredientId,
    };

    onChange([...otherGroupSelections, newSelection]);
  };

  const handleMultipleChoice = (option: ProductOption) => {
    if (!option.available) return;

    const isSelected = isOptionSelected(option.id);
    const otherGroupSelections = selectedOptions.filter(
      (s) => s.optionGroupId !== optionGroup.id
    );

    if (isSelected) {
      const updatedGroupSelections = groupSelections.filter(
        (s) => s.optionId !== option.id
      );
      onChange([...otherGroupSelections, ...updatedGroupSelections]);
    } else {
      if (
        optionGroup.maxSelections &&
        groupSelections.length >= optionGroup.maxSelections
      ) {
        return;
      }

      const newSelection: SelectedOption = {
        optionGroupId: optionGroup.id,
        optionGroupName: optionGroup.name,
        optionId: option.id,
        optionName: option.name,
        quantity: 1,
        priceModifier: option.priceModifier,
        ingredientId: option.ingredientId,
      };
      onChange([...otherGroupSelections, ...groupSelections, newSelection]);
    }
  };

  const handleQuantityChange = (option: ProductOption, newQuantity: number) => {
    if (!option.available) return;
    if (newQuantity < 0) return;

    const otherGroupSelections = selectedOptions.filter(
      (s) => s.optionGroupId !== optionGroup.id
    );

    if (newQuantity === 0) {
      const updatedGroupSelections = groupSelections.filter(
        (s) => s.optionId !== option.id
      );
      onChange([...otherGroupSelections, ...updatedGroupSelections]);
    } else {
      const existingSelection = groupSelections.find(
        (s) => s.optionId === option.id
      );

      if (existingSelection) {
        const updatedGroupSelections = groupSelections.map((s) =>
          s.optionId === option.id ? { ...s, quantity: newQuantity } : s
        );
        onChange([...otherGroupSelections, ...updatedGroupSelections]);
      } else {
        const newSelection: SelectedOption = {
          optionGroupId: optionGroup.id,
          optionGroupName: optionGroup.name,
          optionId: option.id,
          optionName: option.name,
          quantity: newQuantity,
          priceModifier: option.priceModifier,
          ingredientId: option.ingredientId,
        };
        onChange([...otherGroupSelections, ...groupSelections, newSelection]);
      }
    }
  };

  const renderOption = (option: ProductOption) => {
    const optionName = getOptionName(option, language);
    const isSelected = isOptionSelected(option.id);
    const quantity = getOptionQuantity(option.id);

    // SINGLE_CHOICE
    if (optionGroup.type === OptionGroupType.SINGLE_CHOICE) {
      return (
        <motion.div
          key={option.id}
          whileTap={option.available ? { scale: 0.98 } : {}}
          onClick={() => handleSingleChoice(option)}
          className="relative px-3 py-2.5 rounded-xl transition-all cursor-pointer"
          style={{
            backgroundColor: isSelected ? `${colors.primary}15` : `${colors.text}05`,
            border: `1px solid ${isSelected ? colors.primary : colors.border}`,
            opacity: option.available ? 1 : 0.4,
            cursor: option.available ? 'pointer' : 'not-allowed',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Radio */}
              <div
                className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0"
                style={{
                  borderColor: isSelected ? colors.primary : `${colors.text}30`,
                  backgroundColor: isSelected ? colors.primary : 'transparent',
                }}
              >
                {isSelected && (
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                )}
              </div>

              <div className="min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: colors.text }}>
                  {optionName}
                </p>
                {option.allergens && option.allergens.length > 0 && (
                  <p className="text-[10px] truncate" style={{ color: `${colors.text}40` }}>
                    {option.allergens.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {option.priceModifier !== 0 && (
              <span
                className="text-xs font-semibold shrink-0"
                style={{ color: option.priceModifier > 0 ? '#4ade80' : '#f87171' }}
              >
                {option.priceModifier > 0 ? '+' : ''}€{option.priceModifier.toFixed(2)}
              </span>
            )}
          </div>
        </motion.div>
      );
    }

    // MULTIPLE_CHOICE
    if (optionGroup.type === OptionGroupType.MULTIPLE_CHOICE) {
      return (
        <motion.div
          key={option.id}
          whileTap={option.available ? { scale: 0.98 } : {}}
          onClick={() => handleMultipleChoice(option)}
          className="relative px-3 py-2.5 rounded-xl transition-all cursor-pointer"
          style={{
            backgroundColor: isSelected ? `${colors.primary}15` : `${colors.text}05`,
            border: `1px solid ${isSelected ? colors.primary : colors.border}`,
            opacity: option.available ? 1 : 0.4,
            cursor: option.available ? 'pointer' : 'not-allowed',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Checkbox */}
              <div
                className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0"
                style={{
                  borderColor: isSelected ? colors.primary : `${colors.text}30`,
                  backgroundColor: isSelected ? colors.primary : 'transparent',
                }}
              >
                {isSelected && <Check size={10} className="text-white" />}
              </div>

              <div className="min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: colors.text }}>
                  {optionName}
                </p>
                {option.allergens && option.allergens.length > 0 && (
                  <p className="text-[10px] truncate" style={{ color: `${colors.text}40` }}>
                    {option.allergens.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {option.priceModifier !== 0 && (
              <span
                className="text-xs font-semibold shrink-0"
                style={{ color: option.priceModifier > 0 ? '#4ade80' : '#f87171' }}
              >
                {option.priceModifier > 0 ? '+' : ''}€{option.priceModifier.toFixed(2)}
              </span>
            )}
          </div>
        </motion.div>
      );
    }

    // QUANTITY
    if (optionGroup.type === OptionGroupType.QUANTITY) {
      return (
        <motion.div
          key={option.id}
          className="relative px-3 py-2.5 rounded-xl transition-all"
          style={{
            backgroundColor: quantity > 0 ? `${colors.primary}15` : `${colors.text}05`,
            border: `1px solid ${quantity > 0 ? colors.primary : colors.border}`,
            opacity: option.available ? 1 : 0.4,
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" style={{ color: colors.text }}>
                {optionName}
              </p>
              {option.priceModifier !== 0 && (
                <p className="text-[10px]" style={{ color: `${colors.text}40` }}>
                  {option.priceModifier > 0 ? '+' : ''}€{option.priceModifier.toFixed(2)} cad.
                </p>
              )}
            </div>

            {/* Quantity Controls */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleQuantityChange(option, quantity - 1)}
                disabled={!option.available || quantity === 0}
                className="w-6 h-6 rounded-md flex items-center justify-center disabled:opacity-30 transition-colors"
                style={{ backgroundColor: `${colors.text}10` }}
              >
                <Minus size={12} style={{ color: colors.textMuted }} />
              </button>

              <span className="w-5 text-center text-sm font-bold" style={{ color: colors.text }}>
                {quantity}
              </span>

              <button
                onClick={() => handleQuantityChange(option, quantity + 1)}
                disabled={!option.available}
                className="w-6 h-6 rounded-md flex items-center justify-center disabled:opacity-30 transition-colors"
                style={{ backgroundColor: colors.primary }}
              >
                <Plus size={12} className="text-white" />
              </button>
            </div>
          </div>
        </motion.div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-2">
      {/* Group Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>
            {optionGroup.name}
            {optionGroup.required && (
              <span style={{ color: colors.primary }} className="ml-1">*</span>
            )}
          </h3>
          {optionGroup.type === OptionGroupType.MULTIPLE_CHOICE && (
            <p className="text-[10px] mt-0.5" style={{ color: `${colors.text}40` }}>
              {optionGroup.minSelections && optionGroup.maxSelections
                ? `${optionGroup.minSelections}-${optionGroup.maxSelections} opzioni`
                : optionGroup.minSelections
                ? `Min. ${optionGroup.minSelections}`
                : optionGroup.maxSelections
                ? `Max. ${optionGroup.maxSelections}`
                : 'Opzionale'}
            </p>
          )}
        </div>

        {optionGroup.type === OptionGroupType.MULTIPLE_CHOICE &&
          optionGroup.maxSelections && (
            <span className="text-xs" style={{ color: colors.textMuted }}>
              {groupSelections.length}/{optionGroup.maxSelections}
            </span>
          )}
      </div>

      {/* Error */}
      {error && (
        <div
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}
        >
          <AlertCircle size={12} className="text-red-400" />
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Options */}
      <div className="space-y-1.5">
        {optionGroup.options
          .filter((opt) => opt.available || isOptionSelected(opt.id))
          .map((option) => renderOption(option))}
      </div>
    </div>
  );
}
